import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  ScopeResponseRoom,
  ScopeResponseLineItem,
  NoMatchItem,
} from '../../types';
import { ScopeLineItem } from './ScopeLineItem';
import { ScopeDebugDrawer } from './ScopeDebugDrawer';
import { groupItemsBySection } from './ScopeListView';
import { Card, CardContent, CardHeader } from '../ui/Card';

const NoMatchCardRow: React.FC<{
  item: NoMatchItem;
  showDebug: boolean;
  stepMode: boolean;
}> = ({ item, showDebug, stepMode }) => {
  const [open, setOpen] = useState(false);
  const hasDebug = showDebug && Boolean(item.debug);

  return (
    <li>
      <div className="flex items-start gap-2">
        {hasDebug && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="mt-0.5 shrink-0 rounded p-0.5 text-purple-700 hover:bg-purple-100"
            aria-label={open ? 'Hide pipeline trace' : 'Show pipeline trace'}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}
        <div className="flex-1">
          <span className="font-medium">{item.activity_description}</span>
          <span className="block text-red-700">{item.reason}</span>
          {hasDebug && open && item.debug && (
            <ScopeDebugDrawer debug={item.debug} stepMode={stepMode} />
          )}
        </div>
      </div>
    </li>
  );
};

// ================================================================= //
//          RoomCard Component                                         //
// ================================================================= //

interface RoomCardProps {
  room: ScopeResponseRoom;
  defaultExpanded?: boolean;
  showSources?: boolean;
  showDebug?: boolean;
  stepMode?: boolean;
}

/**
 * A collapsible card for a single room.
 * Shows room name, item count, expand/collapse chevron, and copy button.
 * Items are sorted by heading order within the card.
 */
const RoomCard: React.FC<RoomCardProps> = ({
  room,
  defaultExpanded = true,
  showSources = false,
  showDebug = false,
  stepMode = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  /**
   * Copies all room descriptions and notes to clipboard.
   */
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const lines: string[] = [`--- ${room.room_name} (${room.room_type}) ---`];
    for (const item of room.items) {
      lines.push(`  ${item.description}`);
      if (item.note) {
        lines.push(`    Note: ${item.note}`);
      }
    }
    if (room.notes.length > 0) {
      lines.push(`  Notes: ${room.notes.join('; ')}`);
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Reason: Fallback for environments where clipboard API is unavailable
    }
  };

  return (
    <Card>
      <CardHeader className="p-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50 rounded-t-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            )}
            <div>
              <h3 className="text-base font-semibold text-foreground">
                {room.room_name}
              </h3>
              <span className="text-xs text-muted-foreground">{room.room_type}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
              {room.items.length} items
            </span>
            <button
              onClick={handleCopy}
              className={cn(
                'p-1 rounded hover:bg-gray-200 transition-colors',
                'text-muted-foreground hover:text-foreground'
              )}
              title="Copy room descriptions"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0 pb-4 px-4">
          {/* Line items grouped by section with workflow ordering */}
          <div className="space-y-3">
            {groupItemsBySection(room.items).map((group) => (
              <div key={group.section}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-1 mb-1.5">
                  {group.label}
                </h4>
                <div className="space-y-1.5">
                  {group.items.map((item, idx) => (
                    <ScopeLineItem
                      key={idx}
                      item={item}
                      showSources={showSources}
                      showDebug={showDebug}
                      stepMode={stepMode}
                      compact={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* No-match items — activities the LLM identified but retrieval couldn't map (RULE-001) */}
          {room.no_match_items && room.no_match_items.length > 0 && (
            <div className="mt-3 rounded border-l-4 border-l-red-400 bg-red-50 px-3 py-2">
              <p className="text-xs font-medium text-red-900">
                Activities needing manual code lookup:
              </p>
              <ul className="mt-1 text-xs text-red-800 space-y-1">
                {room.no_match_items.map((nm, i) => (
                  <NoMatchCardRow
                    key={i}
                    item={nm}
                    showDebug={showDebug}
                    stepMode={stepMode}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Room notes */}
          {room.notes.length > 0 && (
            <div className="mt-3 rounded bg-slate-50 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">Notes:</p>
              <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
                {room.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ================================================================= //
//          ScopeCardView Component                                    //
// ================================================================= //

interface ScopeCardViewProps {
  rooms: ScopeResponseRoom[];
  generalConditions?: ScopeResponseLineItem[];
  showSources?: boolean;
  showDebug?: boolean;
  stepMode?: boolean;
}

/**
 * Card-based view with collapsible room cards.
 * All cards expanded by default, user can collapse individual ones.
 * Items within each card sorted by heading order.
 */
export const ScopeCardView: React.FC<ScopeCardViewProps> = ({
  rooms,
  generalConditions = [],
  showSources = false,
  showDebug = false,
  stepMode = false,
}) => {
  if (rooms.length === 0 && generalConditions.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-8 text-center text-muted-foreground">
        No scope items to display.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {rooms.map((room, idx) => (
        <RoomCard
          key={idx}
          room={room}
          showSources={showSources}
          showDebug={showDebug}
          stepMode={stepMode}
        />
      ))}

      {/* General conditions card */}
      {generalConditions.length > 0 && (
        <GeneralConditionsCard
          items={generalConditions}
          showSources={showSources}
          showDebug={showDebug}
          stepMode={stepMode}
        />
      )}
    </div>
  );
};

// ================================================================= //
//          GeneralConditionsCard                                       //
// ================================================================= //

const GeneralConditionsCard: React.FC<{
  items: ScopeResponseLineItem[];
  showSources?: boolean;
  showDebug?: boolean;
  stepMode?: boolean;
}> = ({ items, showSources = false, showDebug = false, stepMode = false }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card>
      <CardHeader className="p-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50 rounded-t-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            )}
            <h3 className="text-base font-semibold text-foreground">
              General Conditions
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
            {items.length} items
          </span>
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0 pb-4 px-4">
          <div className="space-y-3">
            {groupItemsBySection(items).map((group) => (
              <div key={group.section}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-1 mb-1.5">
                  {group.label}
                </h4>
                <div className="space-y-1.5">
                  {group.items.map((item, idx) => (
                    <ScopeLineItem
                      key={idx}
                      item={item}
                      showSources={showSources}
                      showDebug={showDebug}
                      stepMode={stepMode}
                      compact={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
